// Canonical option lists for enumerated organization-profile fields.
//
// Lives here rather than in src/ because the import extractor (a Deno edge
// function) must validate model output against exactly the list the editor
// offers. A suggestion carrying a benefit label the editor cannot render is
// worse than no suggestion — the admin accepts it and it silently never appears.
//
// src/lib/orgProfileOptions.ts re-exports this for browser code, so components
// import "@/lib/orgProfileOptions" rather than reaching across the boundary.
//
// SERVICE_TAG_OPTIONS was previously declared twice, in BrandBasicsSection.tsx
// and OrgOnboardingStep5.tsx. They happened to agree; nothing kept them that way.

//
// The stored field is still called serviceTags / service_tags, but it holds
// culture tags only: what it is like to work there, never who the clients are
// (property types, specialties) or how the org is structured (that has its own
// field). Spec: docs/superpowers/specs/2026-10-28-culture-tags-design.md.
export const SERVICE_TAG_OPTIONS = [
  "New Agent Friendly",
  "Mentorship-driven",
  "Tech Forward",
  "Top Producing",
  "Boutique",
  "Referral-first",
  "Collaborative",
  "High Accountability",
  "Community-involved",
  "Family-owned",
] as const;

/** Stored spellings that mean a culture tag under another name. Keys are lower-case. */
const CULTURE_TAG_ALIASES: Record<string, string> = {
  mentorship: "Mentorship-driven",
};

const CULTURE_TAG_BY_LOWER = new Map<string, string>(
  SERVICE_TAG_OPTIONS.map((t) => [t.toLowerCase(), t]),
);

/**
 * The culture tags among stored service tags, in list order, without
 * duplicates. Older values (property types, specialties, structure) are left
 * out rather than shown as culture. Every reader goes through this.
 */
export function cultureTags(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const found = new Set<string>();
  for (const v of values) {
    if (typeof v !== "string") continue;
    const key = v.trim().toLowerCase();
    const tag = CULTURE_TAG_BY_LOWER.get(key) ?? CULTURE_TAG_ALIASES[key];
    if (tag) found.add(tag);
  }
  return SERVICE_TAG_OPTIONS.filter((t) => found.has(t));
}

/**
 * Every benefit label the editor offers, grouped under the headings
 * BenefitsSection renders, in its order.
 *
 * `benefitGroups` in
 * src/components/admin/profile-editor/sections/BenefitsSection.tsx is the visual
 * source of truth (it pairs each label with an icon). This list must stay in
 * step with it, and src/lib/orgProfileOptions.parity.test.ts fails if it drifts.
 *
 * Note the source lists "In-house Legal / Compliance Support" in two groups —
 * Professional Support and Culture & Extras. It appears once here, under the
 * first, because these lists are used for set membership and for a picker
 * that must offer each value exactly once.
 */
export const BENEFIT_GROUPS: readonly { title: string; labels: readonly string[] }[] = [
  {
    title: "Health & Financial Benefits",
    labels: [
      "Health Insurance",
      "Dental Insurance",
      "Vision Insurance",
      "Life Insurance",
      "Retirement Plan / 401k",
      "FSA / HSA",
      "Disability Insurance",
      "E&O Insurance Included",
      "NAR/CAR Dues Covered",
      "Coaching Stipend",
    ],
  },
  {
    title: "Time Off & Flexibility",
    labels: [
      "Unlimited PTO",
      "Paid Holidays",
      "Paid Parental Leave",
      "Flex Hours",
      "Remote Work Options",
      "Hybrid Schedule",
    ],
  },
  {
    title: "Office Amenities",
    labels: [
      "Free Coffee & Snacks",
      "On-site Gym / Fitness Center",
      "Parking",
      "Secured Access / Key Fob Entry",
      "Printers, Copiers & Scanners",
      "Conference Rooms",
      "Meeting Rooms",
      "Private Offices",
      "Green Room",
      "Podcast Studio",
      "Photography Studio",
      "Training Room",
      "Break Room / Lounge",
      "Rooftop / Outdoor Space",
      "EV Charging Stations",
      "Bike Storage",
    ],
  },
  {
    title: "Professional Support",
    labels: [
      "Transaction Coordinator Included",
      "In-house Legal / Compliance Support",
      "Admin / Assistant Support",
      "Marketing Coordinator",
      "Dedicated Onboarding Team",
    ],
  },
  {
    title: "Professional Development",
    labels: [
      "Professional Development Budget",
      "Mentorship Program",
      "Licensing Fee Reimbursement",
      "Conference & Event Access",
      "Training & Certifications",
    ],
  },
  {
    title: "Culture & Extras",
    labels: [
      "Team Events",
      "Client Appreciation Budget",
      "Marketing Support",
      "Lead Generation Tools",
      "Transaction Coordinator Support",
      "Agent Recognition Program",
      "Onboarding & Orientation",
    ],
  },
];

/** The flat, deduplicated benefit list — the extractor's allow-list. */
export const BENEFIT_OPTIONS: readonly string[] = BENEFIT_GROUPS.flatMap((g) => g.labels);

/**
 * Compensation model keys and the labels the editor shows for them.
 * CompensationSection pairs each with an icon; OrgOnboardingStep4 and the
 * Console's edit-on-behalf form offer the same list. Never a split percentage
 * or dollar figure — model shape only.
 */
export const COMPENSATION_MODEL_OPTIONS: readonly { key: string; label: string }[] = [
  { key: "flat_split", label: "Fixed Commission" },
  { key: "capping_model", label: "Capping Model" },
  { key: "100_commission", label: "100% Commission" },
  { key: "tiered_splits", label: "Tiered Commission" },
  { key: "profit_share", label: "Profit Share" },
  { key: "revenue_share", label: "Revenue Share" },
  { key: "stock_equity", label: "Stock / Equity Options" },
  { key: "signing_bonus", label: "Signing Bonus" },
  { key: "transaction_bonuses", label: "Transaction Bonuses" },
  { key: "team_override", label: "Team Override" },
  { key: "salary", label: "Salary" },
  { key: "draw_against_commission", label: "Draw Against Commission" },
  { key: "company_lead_commission", label: "Company Lead Commission" },
];

/** TrainingSection's cadence chips; stored as string[] in training_frequency. */
export const TRAINING_FREQUENCY_OPTIONS = ["Daily", "Weekly", "Bi-weekly", "Monthly"] as const;

/** SupportSection's marketing-support chips. */
export const MARKETING_SUPPORT_OPTIONS = [
  "Professional photography",
  "Social media templates",
  "Listing presentations",
  "Video production",
  "Print materials",
  "Email campaigns",
  "Branding kit",
] as const;

